import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import classNames from "classnames/bind";
import {
  addYears,
  eachMonthOfInterval,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfYear,
  subYears,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { Button } from "../../../Button";
import styles from "../DateSelector.module.scss";
import { NOW } from "../dateSelectorConstants";
import type { RangePickerProps } from "../types";

const cx = classNames.bind(styles);

const MonthPicker = ({
  value,
  onChange,
  onClose,
  isRange,
  min: minDate,
  max: maxDate,
  isClearable = false,
}: RangePickerProps) => {
  const [min, setMin] = useState<Date | undefined>();
  const [endDateOnMouseEnter, setEndDateOnMouseEnter] = useState<
    Date | undefined
  >();

  const [baseValue, setBaseValue] = useState(
    value ? (value instanceof Date ? value : value?.from) : NOW
  );

  const options = useMemo(() => {
    const minMonth = startOfYear(baseValue);
    const maxMonth = endOfYear(baseValue);

    return eachMonthOfInterval({ start: minMonth, end: maxMonth });
  }, [baseValue]);

  const middleValue = useMemo(
    () => options[Math.floor(options.length / 2)],
    [options]
  );

  const onClick = (newValue: Date) => {
    const shouldSetStart =
      !value?.from ||
      !min ||
      isBefore(newValue, min) ||
      (value?.to && isBefore(newValue, value.to)) ||
      (value instanceof Date && isBefore(newValue, value));

    if (shouldSetStart) {
      onChange({
        from: startOfMonth(newValue),
        to: isRange ? undefined : endOfMonth(newValue),
      });

      if (isRange) {
        setMin(startOfMonth(newValue));
      } else {
        onClose();
      }
    } else {
      onChange({ ...value, to: endOfMonth(newValue) });

      onClose();
    }
  };

  const onNextOptionsClick = () => {
    setBaseValue(addYears(baseValue, 1));
  };

  const onPreviousOptionsClick = () => {
    setBaseValue(subYears(baseValue, 1));
  };

  const onMouseEnter = (newDate: Date) => {
    if (isRange && min && isAfter(newDate, min)) {
      setEndDateOnMouseEnter(newDate);
    }
  };

  const onMouseLeave = () => {
    if (endDateOnMouseEnter) {
      setEndDateOnMouseEnter(undefined);
    }
  };

  return (
    <div className={cx("yearPickerContainer")}>
      <div className={cx("yearPickerHeader")}>
        <button
          type="button"
          className={cx("caretButton")}
          onClick={onPreviousOptionsClick}
        >
          <CaretLeft />
        </button>

        <p className={cx("decadeRange")}>{format(options[0], "yyyy")}</p>

        <button
          type="button"
          className={cx("caretButton")}
          onClick={onNextOptionsClick}
        >
          <CaretRight />
        </button>
      </div>

      <div className={cx("yearPickerCalendar")}>
        {options.map((date) => (
          <button
            type="button"
            key={date.toString()}
            className={cx("calendarItem", "year", {
              isInWindow: isWithinInterval(date, {
                start: startOfYear(middleValue),
                end: endOfYear(middleValue),
              }),
              isEndSelected:
                (value?.from && isSameMonth(date, value.from)) ||
                (value?.to && isSameMonth(date, value.to)),
              ...(isRange
                ? {
                    isSelected:
                      (value?.from &&
                        value?.to &&
                        isWithinInterval(date, {
                          start: value?.from,
                          end: value?.to,
                        })) ||
                      (value?.from &&
                        endDateOnMouseEnter &&
                        isWithinInterval(date, {
                          start: value?.from,
                          end: endDateOnMouseEnter,
                        })),
                  }
                : { isSelected: value?.from && isSameMonth(date, value.from) }),
            })}
            onClick={() => onClick(date)}
            onMouseEnter={() => onMouseEnter(date)}
            onMouseLeave={() => onMouseLeave()}
            disabled={
              (minDate && isBefore(date, minDate)) ||
              (maxDate && isAfter(date, maxDate))
            }
          >
            {format(date, "MMM")}
          </button>
        ))}
      </div>
      {isClearable && (
        <div className={cx("footer")}>
          <Button
            variant="outline"
            onClick={() => {
              onChange(undefined);
              onClose();
            }}
            className={cx("clear")}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
